export interface TecmoTeam {
  abbreviation: string
  name: string
  /**
   * Zero-based column of this team's helmet in `public/helmets.png`.
   *
   * @remarks
   * The sprite sheet is ordered alphabetically by team nickname, which does not
   * match the ROM ordering this list uses, so each column is stated explicitly
   * rather than derived from the array position.
   */
  helmetIndex: number
}

/**
 * The 28 Tecmo Super Bowl franchises available to any player in any game.
 *
 * @remarks
 * Listed in ROM order, which is how the game itself presents the teams. The
 * abbreviations are the exact strings the save-state parser writes to
 * `game_participants.team_abbreviation`, so they double as the lookup key for
 * helmet rendering — note `RAMS` rather than the modern `LA`. Results with no
 * franchise use `null` / Unknown and are not in this list, so they have no helmet.
 */
export const TECMO_TEAMS: TecmoTeam[] = [
  { abbreviation: 'BUF', name: 'Buffalo', helmetIndex: 3 },
  { abbreviation: 'IND', name: 'Indianapolis', helmetIndex: 10 },
  { abbreviation: 'MIA', name: 'Miami', helmetIndex: 12 },
  { abbreviation: 'NE', name: 'New England', helmetIndex: 20 },
  { abbreviation: 'NYJ', name: 'NY Jets', helmetIndex: 16 },
  { abbreviation: 'CIN', name: 'Cincinnati', helmetIndex: 2 },
  { abbreviation: 'CLE', name: 'Cleveland', helmetIndex: 5 },
  { abbreviation: 'HOU', name: 'Houston', helmetIndex: 18 },
  { abbreviation: 'PIT', name: 'Pittsburgh', helmetIndex: 26 },
  { abbreviation: 'DEN', name: 'Denver', helmetIndex: 4 },
  { abbreviation: 'KC', name: 'Kansas City', helmetIndex: 9 },
  { abbreviation: 'RAI', name: 'LA Raiders', helmetIndex: 21 },
  { abbreviation: 'SD', name: 'San Diego', helmetIndex: 8 },
  { abbreviation: 'SEA', name: 'Seattle', helmetIndex: 25 },
  { abbreviation: 'WAS', name: 'Washington', helmetIndex: 23 },
  { abbreviation: 'NYG', name: 'NY Giants', helmetIndex: 15 },
  { abbreviation: 'PHI', name: 'Philadelphia', helmetIndex: 13 },
  { abbreviation: 'PHX', name: 'Phoenix', helmetIndex: 7 },
  { abbreviation: 'DAL', name: 'Dallas', helmetIndex: 11 },
  { abbreviation: 'CHI', name: 'Chicago', helmetIndex: 1 },
  { abbreviation: 'DET', name: 'Detroit', helmetIndex: 17 },
  { abbreviation: 'GB', name: 'Green Bay', helmetIndex: 19 },
  { abbreviation: 'MIN', name: 'Minnesota', helmetIndex: 27 },
  { abbreviation: 'TB', name: 'Tampa Bay', helmetIndex: 6 },
  { abbreviation: 'SF', name: 'San Francisco', helmetIndex: 0 },
  { abbreviation: 'RAMS', name: 'LA Rams', helmetIndex: 22 },
  { abbreviation: 'NO', name: 'New Orleans', helmetIndex: 24 },
  { abbreviation: 'ATL', name: 'Atlanta', helmetIndex: 14 },
]

/** Number of helmet columns in the sprite sheet. */
export const HELMET_COLUMN_COUNT = TECMO_TEAMS.length

const TEAMS_BY_ABBREVIATION = new Map(
  TECMO_TEAMS.map(team => [team.abbreviation, team])
)

/**
 * Aliases for abbreviations that reach the UI from somewhere other than the
 * save-state parser, such as hand-entered champion teams or Excel imports.
 */
const ABBREVIATION_ALIASES: Record<string, string> = {
  LA: 'RAMS',
  LAR: 'RAMS',
  STL: 'RAMS',
  LVR: 'RAI',
  OAK: 'RAI',
  LAC: 'SD',
  ARI: 'PHX',
  WSH: 'WAS',
  JETS: 'NYJ',
  GIA: 'NYG',
  TEN: 'HOU',
  BAL: 'CLE',
}

/**
 * Tokens that mean no franchise was recorded. Not in the sprite sheet.
 *
 * @remarks
 * Folded to uppercase without punctuation, matching how a form or import
 * might spell the same blank — `"n/a"`, `"UNK"`, `"Unknown"`.
 */
const UNKNOWN_TEAM_TOKENS = new Set(['UNK', 'UNKNOWN', 'NONE', 'NA'])

/**
 * True when a result has no franchise, so {@link TeamHelmet} must render nothing.
 *
 * @param abbreviation - Stored value, form value, or import cell.
 *
 * @example
 * ```typescript
 * isUnknownTeam(null)      // true
 * isUnknownTeam('Unknown') // true
 * isUnknownTeam('SF')      // false
 * ```
 */
export function isUnknownTeam(abbreviation?: string | null): boolean {
  if (!abbreviation?.trim()) {
    return true
  }

  const normalized = abbreviation.trim().toUpperCase().replace(/[^A-Z0-9]/g, '')
  return UNKNOWN_TEAM_TOKENS.has(normalized)
}

/**
 * Resolves any reasonable spelling of a team to its canonical Tecmo entry.
 *
 * @param abbreviation - Raw value from the database, an import, or a form.
 * @returns The matching team, or `null` when the value is absent or unknown.
 *
 * @example
 * ```typescript
 * resolveTecmoTeam('la')?.abbreviation // 'RAMS'
 * resolveTecmoTeam('Unknown') // null
 * resolveTecmoTeam('XYZ') // null
 * ```
 */
export function resolveTecmoTeam(
  abbreviation?: string | null
): TecmoTeam | null {
  if (!abbreviation || isUnknownTeam(abbreviation)) {
    return null
  }

  const normalized = abbreviation.trim().toUpperCase()
  const canonical = ABBREVIATION_ALIASES[normalized] ?? normalized

  return TEAMS_BY_ABBREVIATION.get(canonical) ?? null
}
